import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormHelperText,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { WarningAmberOutlined } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import { createContext, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormValue } from "./types";
import React from "react";
import { initializeFormData } from "./helpers";
import { formReducer } from "./reducers/FormReducer";
import { z } from "zod";
import { observer } from "mobx-react";
import { ErrorData, ErrorInfo } from "./types";
import { validationReducer } from "./reducers/ValidationReducer";
import type { Action as ValidationAction } from "./reducers/ValidationReducer";
import type { Action as FormAction } from "./reducers/FormReducer";
import AlertDialog from "../AlertDialog";

interface CustomFormProps<A extends z.ZodType<any, any, any>> {
  children: React.ReactNode;
  readOnly?: boolean;
  zodDataSchema?: A;
  currentData?: A | null | undefined;
  initialValues?: A | null | undefined;
  onSave?: (data: A) => void;
  onUpdate?: (data: A) => void;
  onClose?: () => void;
  onRemove?: () => void;
  onDeSelect?: () => void;
  reservedIds?: string[];
  modal?: boolean;
  disableValidation?: boolean;
  hideDeleteAndEditButtons?: boolean;
  hideSaveButton?: boolean;
  hideReadOnlyChip?: boolean;
}

const CustomFormContext = createContext<{
  getValidationErrors: (name: string) => ErrorInfo[] | null;
  setDataDispatch: React.Dispatch<FormAction<any>>;
  setErrorsDispatch: React.Dispatch<ValidationAction<any>>;
  dataIsSet: boolean;
  errorsAreSet: boolean;
  readOnlyState: boolean;
  modal?: boolean;
  tempData: any;
  fieldValue: (name: string, locale?: string) => any | undefined;
  onSave?: (data: any) => void;
  onUpdate?: (data: any) => void;
  showErrors: boolean;
}>({
  getValidationErrors: (name: string) => null,
  setDataDispatch: () => {},
  setErrorsDispatch: () => {},
  dataIsSet: false,
  errorsAreSet: false,
  readOnlyState: false,
  modal: false,
  tempData: {},
  fieldValue: (name: string, locale?: string) => {},
  onSave: () => {},
  onUpdate: () => {},
  showErrors: false,
});

const defaultReducer = (state: any, action: any) => state;

const CustomForm = observer(
  <A extends z.ZodType<any, any, any>>({
    children,
    readOnly = false,
    zodDataSchema,
    currentData,
    modal = false,
    disableValidation = false,
    hideDeleteAndEditButtons = false,
    hideSaveButton = false,
    hideReadOnlyChip = false,
    initialValues = undefined,
    onClose,
    onSave,
    onRemove,
    onDeSelect,
    onUpdate,
  }: CustomFormProps<A>) => {
    const { t } = useTranslation();
    const [readOnlyState, setReadOnlyState] = useState<boolean>(readOnly);
    const [removeOpen, setRemoveOpen] = useState<boolean>(false);
    const [discardChangesOpen, setDiscardChangesOpen] =
      useState<boolean>(false);
    const initialState = {} as unknown as z.infer<A>;
    const [originalData, setOriginalData] = useState<z.infer<A> | null>(null);
    const [data, setDataDispatch] = useReducer(
      zodDataSchema ? formReducer : defaultReducer,
      initialState
    );
    const [showErrors, setShowErrors] = useState<boolean>(false);
    const [errors, setErrorsDispatch] = useReducer(
      validationReducer,
      [] as ErrorData[]
    );
    const [dataIsSet, setDataIsSet] = useState<boolean>(false);
    const [errorsAreSet, setErrorsAreSet] = useState<boolean>(false);
    const existingData = !currentData
      ? ({} as unknown as z.infer<A>)
      : (currentData as unknown as z.infer<A>);

    const isValid = () => {
      if (zodDataSchema) {
        const result = zodDataSchema?.safeParse(data);
        return (
          result?.success &&
          errors.filter((a) => a.errors.length > 0).length === 0
        );
      }
      return errors.filter((a) => a.errors.length > 0).length === 0;
    };

    const refreshValidationErrors = () => {
      if (!disableValidation) {
        setErrorsDispatch({
          type: "REFRESH_VALIDATION",
          payload: { tempData: data, isNew: currentData ? true : false },
        });
      }
    };

    React.useEffect(() => {
      if (dataIsSet && data && errorsAreSet && errors) {
        if (!disableValidation) {
          setShowErrors(false);
          refreshValidationErrors();
        }
      }
    }, [setDataDispatch, data]);

    const getValidationErrors = (alias: string) => {
      if (!errors || !errorsAreSet) {
        return null;
      }

      const errorData = errors.find((a) => a.alias === alias);
      if (showErrors && errorData) {
        return errorData.errors;
      }
      return null;
    };

    const handleResetForm = () => {
      setShowErrors(false);
      if (originalData) {
        const { populatedData, fieldDatas } = initializeFormData(
          children as any,
          originalData
        );
        setDataDispatch({
          type: "INITIALIZE_DATA",
          payload: populatedData,
        });
        if (fieldDatas && populatedData) {
          setErrorsDispatch({
            type: "INITIALIZE_VALIDATION",
            payload: {
              fieldDatas,
              populatedData,
              isNew: currentData ? true : false,
            },
          });
        }
      }
      setDiscardChangesOpen(false);
      if (onDeSelect) {
        onDeSelect();
      }
    };

    React.useEffect(() => {
      setShowErrors(false);
      if (children !== null && children !== undefined) {
        const { populatedData, fieldDatas } = initializeFormData(
          children as any,
          existingData,
          initialValues
        );
        setOriginalData(populatedData);
        setDataDispatch({
          type: "INITIALIZE_DATA",
          payload: populatedData,
        });
        if (fieldDatas && populatedData) {
          setErrorsDispatch({
            type: "INITIALIZE_VALIDATION",
            payload: {
              fieldDatas,
              populatedData,
              isNew: currentData ? true : false,
            },
          });
        }
      }
    }, []);

    React.useEffect(() => {
      setShowErrors(false);
      if (children !== null && children !== undefined) {
        const { populatedData, fieldDatas } = initializeFormData(
          children as any,
          existingData,
          initialValues
        );
        setDataDispatch({
          type: "INITIALIZE_DATA",
          payload: populatedData,
        });
        setErrorsDispatch({
          type: "INITIALIZE_VALIDATION",
          payload: {
            fieldDatas: fieldDatas,
            populatedData: populatedData,
            isNew: currentData ? true : false,
          },
        });
      }
    }, [currentData]);

    React.useEffect(() => {
      if (!data) {
        return;
      }
      setDataIsSet(true);
    }, [data, data.values]);

    React.useEffect(() => {
      if (!errors) {
        return;
      }
      setErrorsAreSet(true);
    }, [errors]);

    const handleOnSave = () => {
      if (onSave) {
        if (isValid()) {
          onSave(data as A);
          handleResetForm();
        }
        setShowErrors(true);
      }
    };

    const handleOnUpdate = () => {
      if (onUpdate) {
        if (isValid()) {
          onUpdate(data as A);
          handleResetForm();
        }
        setShowErrors(true);
      }
    };

    const handleRemove = () => onRemove && onRemove();
    const fieldValue = (alias: string) => {
      let fieldValue = undefined;

      if (data.hasOwnProperty(alias)) {
        return data[alias];
      } else {
        const existingData = data.values?.find(
          (a: FormValue) => a.fieldAlias === alias
        );
        if (existingData) {
          fieldValue = existingData as FormValue;
        }
        return fieldValue?.value;
      }
    };

    if (!dataIsSet || !errorsAreSet) {
      return <CircularProgress />;
    }
    return (
      <CustomFormContext.Provider
        value={{
          getValidationErrors,
          setDataDispatch,
          setErrorsDispatch,
          dataIsSet,
          errorsAreSet,
          readOnlyState,
          modal,
          tempData: data,
          fieldValue,
          onSave,
          onUpdate,
          showErrors,
        }}
      >
        <div
          style={{ display: "flex" }}
          className={`custom-form-tabs-container-${
            modal ? "MODAL" : "DEFAULT"
          }`}
        >
          <Box className="modal-button-box">
            {(existingData.values?.length > 0 ||
              Object.keys(existingData).filter(
                (a) => a !== "formId" && a !== "values"
              ).length > 0) && (
              <>
                <Chip
                  icon={<WarningAmberOutlined />}
                  label={t("customform.editing_mode")}
                  color="warning"
                  size="medium"
                />
                <IconButton
                  sx={{ marginLeft: 1 }}
                  edge="end"
                  aria-label="edit"
                  onClick={() => setDiscardChangesOpen(true)}
                  data-test="customform-edit-button"
                >
                  <EditIcon />
                  <Typography>{t("customform.cancel")}</Typography>
                </IconButton>
              </>
            )}
            {!hideSaveButton && (
              <Button
                id="top-save-button"
                disabled={
                  readOnlyState ||
                  (errors &&
                    errors.filter((a) => a.errors.length > 0).length > 0)
                }
                onClick={handleOnSave}
                data-test="save-button"
              >
                {t("customform.save")}
              </Button>
            )}
          </Box>
        </div>
        <Grid container margin={2}>
          <Grid container spacing={2}>
            {children}
          </Grid>
          <Stack spacing={2}>
            <Button
              id="bottom-save-button"
              onClick={
                !currentData ||
                (currentData && (currentData as any)?.values?.length === 0)
                  ? handleOnSave
                  : handleOnUpdate
              }
              data-test="save-button"
            >
              Spara
            </Button>
            {showErrors &&
              errors?.map((info, index) => (
                <FormHelperText key={index} variant="standard">
                  {info.errors.length > 0 && (
                    <Typography variant="caption" color="red">
                      {info.alias.split("_").pop()}: {info.errors[0]?.message}
                    </Typography>
                  )}
                </FormHelperText>
              ))}
          </Stack>
        </Grid>
        {/* <pre>
          {JSON.stringify(data, null, 2)}
          {JSON.stringify(errors, null, 2)}
        </pre> */}
        <AlertDialog
          openDialog={removeOpen}
          setOpenDialog={() => setRemoveOpen(false)}
          title={`${t("customform.remove")} title of form`}
          description={t("customform.remove_confirmation_text")}
          action={handleRemove}
          cancelVisible={true}
          data-test="customform-remove-alert"
        />
        <AlertDialog
          openDialog={discardChangesOpen}
          setOpenDialog={() => setDiscardChangesOpen(false)}
          title={`${t("customform.discard_changes")}`}
          description={t("customform.discard_changes_confirmation_text")}
          action={handleResetForm}
          cancelVisible={true}
          data-test="customform-discard-changes-alert"
        />
      </CustomFormContext.Provider>
    );
  }
);

export { CustomFormContext, CustomForm };
